// DriveWrapper.js
import React from "react";
import { InstantSearch, Configure } from "react-instantsearch-hooks-web";
import Drive from "./drive";
import { getClient2 } from "../../search/algolia";
import { getOrgId } from "../../actions/orgsActions";

/**
 * No-op client that satisfies InstantSearch but never talks to Algolia.
 * Searches resolve immediately with empty results, preventing 404s.
 */
const emptyResults = { hits: [], nbHits: 0, processingTimeMS: 0, query: "", params: "" };
const noopSearchClient = {
	search: async () => ({ results: [emptyResults] }),
	searchForFacetValues: async () => ({ results: [] }),
};

/**
 * Small hook that probes whether the index exists.
 * If the probe fails (404 / missing index / network error), we fall back to noop client.
 */
function useSafeAlgolia(indexName, enabled = true) {
	const [state, setState] = React.useState({
		ready: false,
		client: noopSearchClient,
		indexName: "placeholder_index",
	});

	React.useEffect(() => {
		let cancelled = false;

		async function probe() {
			if (!enabled) {
				// Not enabled → stick with noop and mark ready.
				if (!cancelled) { setState({ ready: true, client: noopSearchClient, indexName: "placeholder_index" }); }
				return;
			}

			const realClient = getClient2();

			try {
				// Tiny probe: ask for zero hits; cheap and fast.
				await realClient.search([
					{ indexName, params: { query: "", hitsPerPage: 0, attributesToRetrieve: ["objectID"] } },
				]);

				if (!cancelled) {
					setState({ ready: true, client: realClient, indexName });
				}
			} catch (e) {
				// Index missing or any error → graceful fallback to noop.
				if (!cancelled) {
					setState({ ready: true, client: noopSearchClient, indexName: "placeholder_index" });
				}
			}
		}

		probe();
		return () => {
			cancelled = true;
		};
	}, [indexName, enabled]);

	return state; // { ready, client, indexName }
}

/**
 * Wrapper you can use EVERYWHERE you used <Drive /> before.
 * It auto-detects if the org has an Algolia Drive index and falls back gracefully if not.
 */
export default function DriveWrapper(props) {
	const orgId = getOrgId();

	// Your naming convention (adjust if needed)
	const driveIndex = `prod_DRIVE_org_${orgId}`;

	// Optional gate: if you only want Algolia for specific orgs, toggle here.
	// Set to true to try for everyone; or whitelist orgs if you prefer:
	const shouldTryAlgolia = true;
	// const shouldTryAlgolia = new Set([
	//   "a25f9e42-23cd-11ec-9068-066917b38554",
	//   "75a99644-d769-11ec-8330-069463faa5ff",
	// ]).has(orgId);

	const { ready, client, indexName } = useSafeAlgolia(driveIndex, shouldTryAlgolia);

	// Render immediately with noop client; once probe completes we may switch to the real client.
	// (No loading UI needed; both paths work transparently.)
	return (
		<InstantSearch searchClient={client} indexName={indexName}>
			<Configure hitsPerPage={100} />
			<Drive {...props} />
		</InstantSearch>
	);
}
